import * as v from "valibot";

import type { RewardData } from "@textclub/common/bridge";
import { createConversationId } from "@textclub/common/random-id";

import { instrumentSdkMethod } from "../instrumentation";
import { parseArgs } from "../schema-utils";
import type { ModuleContext } from "./module-context";

const SHOW_REWARDED_AD_TIMEOUT = 15 * 1000;
const REWARDS_TIMEOUT = 10 * 1000;

const CompleteRewardOpts = v.object({
  rewardId: v.pipe(v.string(), v.minLength(1)),
});

/** The AdsModule offers players a reward for playing another Jest game. */
export class AdsModule {
  constructor(private ctx: ModuleContext) {
    this.ctx.registerMessageHandlers({
      ShowRewardedAdResponse: (message) => this.ctx.resolveWaiting(message),
      GetIncompleteRewardsResponse: (message) =>
        this.ctx.resolveWaiting(message),
      CompleteRewardResponse: (message) => this.ctx.resolveWaiting(message),
    });
  }

  @instrumentSdkMethod({ name: "ads.showRewardedAd" })
  async showRewardedAd(): Promise<{ result: "started" | "no_fill" }> {
    this.ctx.assertInitialized();

    const conversationId = createConversationId();
    this.ctx.sendMessage({
      conversationId,
      type: "ShowRewardedAd",
    });

    const response = await this.ctx.waitForNextMessage(
      "ShowRewardedAdResponse",
      conversationId,
      SHOW_REWARDED_AD_TIMEOUT,
    );
    if (!response.success) {
      throw new Error("Failed to show a rewarded ad");
    }
    return { result: response.result };
  }

  @instrumentSdkMethod({ name: "ads.getIncompleteRewards" })
  async getIncompleteRewards(): Promise<{
    rewards: RewardData[];
    rewardsSigned: string;
  }> {
    this.ctx.assertInitialized();

    const conversationId = createConversationId();
    this.ctx.sendMessage({ conversationId, type: "GetIncompleteRewards" });

    const response = await this.ctx.waitForNextMessage(
      "GetIncompleteRewardsResponse",
      conversationId,
      REWARDS_TIMEOUT,
    );
    if (!response.success) {
      throw new Error("Failed to fetch incomplete rewards");
    }
    return { rewards: response.rewards, rewardsSigned: response.rewardsSigned };
  }

  @instrumentSdkMethod({ name: "ads.completeReward" })
  async completeReward(options: {
    rewardId: string;
  }): Promise<
    | { result: "success" }
    | { result: "error"; error: "internal_error" | "invalid_reward" }
  > {
    parseArgs(CompleteRewardOpts, options);

    this.ctx.assertInitialized();

    const conversationId = createConversationId();
    this.ctx.sendMessage({
      conversationId,
      type: "CompleteReward",
      rewardId: options.rewardId,
    });

    const response = await this.ctx.waitForNextMessage(
      "CompleteRewardResponse",
      conversationId,
      REWARDS_TIMEOUT,
    );
    return response.result === "success"
      ? { result: "success" }
      : { result: "error", error: response.error };
  }

  /** Exposed for decorator use - delegates to context */
  instrumentMethod<T>(opts: { method: string; args: unknown; fn: () => T }): T {
    return this.ctx.instrumentMethod(opts);
  }
}
